// src/components/ConnectionsList.js

import React, { useState } from 'react';
import { Box, Typography, List, ListItem, ListItemButton, ListItemAvatar, Avatar, ListItemText, CircularProgress, Badge, IconButton, Menu, MenuItem } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useConnections } from '../hooks/useConnections';
import { usePresenceStatus } from '../hooks/usePresenceStatus';
import { useUnreadCount } from '../hooks/useUnreadCount';
import { auth, db } from '../config/firebase';
import { doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BlockUserDialog from './BlockUserDialog';

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#44b700',
    color: '#44b700',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': { transform: 'scale(.8)', opacity: 1 },
    '100%': { transform: 'scale(2.4)', opacity: 0 },
  },
}));

const OfflineBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#f44336',
    color: '#f44336',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
  },
}));

const ConnectionItem = ({ user, onSelectChat }) => {
  const presence = usePresenceStatus(user.uid);
  const currentUser = auth.currentUser;
  const chatId = currentUser ? [currentUser.uid, user.uid].sort().join('_') : null;
  const unreadCount = useUnreadCount(chatId);
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  if (!currentUser) return null; 

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleBlockClick = () => {
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleBlockConfirm = async () => {
    setDialogOpen(false);
    const currentUserRef = doc(db, 'users', currentUser.uid);
    try {
        await updateDoc(currentUserRef, {
            blockedUsers: arrayUnion(user.uid),
            connections: arrayRemove(user.uid)
        });
    } catch (err) {
        console.error("Error blocking user: ", err);
    }
  };

  const renderAvatarWithStatus = () => {
    if (presence?.state === 'online') {
      return ( <StyledBadge overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} variant="dot"> <Avatar src={user.photoURL} /> </StyledBadge> );
    }
    if (presence?.state === 'offline') {
      return ( <OfflineBadge overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} variant="dot"> <Avatar src={user.photoURL} /> </OfflineBadge> );
    }
    return <Avatar src={user.photoURL} />;
  };

  return (
    <>
      <ListItem 
        disablePadding
        secondaryAction={
          <IconButton edge="end" aria-label="options" onClick={handleMenuClick}>
            <MoreVertIcon />
          </IconButton>
        }
      >
        <ListItemButton onClick={() => onSelectChat(user)}>
          <ListItemAvatar>
            <Badge badgeContent={unreadCount} color="error" anchorOrigin={{ vertical: 'top', horizontal: 'left', }}>
              {renderAvatarWithStatus()}
            </Badge>
          </ListItemAvatar>
          <ListItemText primary={user.displayName} />
        </ListItemButton>
      </ListItem>
      
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleBlockClick} sx={{ color: 'error.main' }}>Block {user.displayName}</MenuItem>
      </Menu>

      <BlockUserDialog 
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleBlockConfirm}
        userName={user.displayName}
      />
    </>
  );
};

const ConnectionsList = ({ onSelectChat }) => {
  const { connections, loading } = useConnections();
  
  if (loading) {
    return <CircularProgress sx={{ mt: 4, display: 'block', margin: 'auto' }} />;
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom>Your Friends</Typography>
      {connections.length === 0 ? (
        <Typography variant="body2" color="text.secondary">You have no friends yet.</Typography>
      ) : (
        <List>
          {connections.map((user) => (
            <ConnectionItem key={user.uid} user={user} onSelectChat={onSelectChat} />
          ))}
        </List>
      )}
    </Box>
  );
};

export default ConnectionsList;